import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Layout, Tooltip, Typography, Form } from 'antd';
import styled from '@emotion/styled';
import { delay } from '~/utils';
import api from '@lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@stores/auth';

const { Title, Text } = Typography;
const { Content } = Layout;

const TermsConditions: React.FC = () => {
  const { t } = useTranslation();
  const { userData, setUserData } = useAuthStore();
  // const [checkTerm, setCheckTerm] = useState(false);
  const [nameLength, setNameLength] = useState(20);
  const [studioName, setStudioName] = useState('');
  const [isDoneSavedName, setDoneSavedName] = useState(true);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    if (userData?.mainChannel) {
      navigate(`/`, { replace: true });
      return;
    }

    setStudioName(userData?.name?.substring(0, 13) + ' Studio');
  }, [navigate, userData]);

  useEffect(() => {
    setNameLength(studioName.trim().length);
    form.setFieldsValue({
      studio_name: studioName,
    });
  }, [form, studioName]);

  const saveStudioName = async () => {
    setDoneSavedName(false);

    await api.post('/channel', { name: studioName.trim() });
    await delay(1000);
    const user = await api.get<typeof userData>('/user', {
      params: {
        opt_fields: 'poseprocessingvideoids',
      },
    });

    setUserData(user.data);
    setDoneSavedName(true);
    navigate(`/`, { replace: true });
  };

  return (
    <ContentBlock>
      <Typography>
        <Title level={2}>{t('terms.notice.title')}</Title>
        <Text>{t('terms.notice.desc')}</Text>
      </Typography>
      <Form
        form={form}
        name="ToS_form"
        layout="vertical"
        hideRequiredMark
        autoComplete="off"
        onFinish={saveStudioName}
      >
        <div className="content">
          {/* <div className="top-content">
              <div className="term-content">{t('terms.notice.title')}</div>
              <Form.Item
                name="check_term"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => (value ? Promise.resolve() : Promise.reject()),
                  },
                ]}
                valuePropName="checked"
                style={{
                  maxWidth: '730px',
                  width: '100%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
                initialValue={false}
              >
                <div className="checkbox-wrap" aria-label={checkTerm ? 'checked' : 'non-checked'}>
                  <Tooltip
                    placement="bottom"
                    title={t('terms.content.checkbox.warning')}
                    visible={!checkTerm}
                    getTooltipContainer={trigger => trigger.parentElement ?? document.body}
                  >
                    <Checkbox checked={checkTerm} onChange={() => setCheckTerm(!checkTerm)}>
                      {t('terms.content.checkbox.title')}
                    </Checkbox>
                  </Tooltip>
                  <Button className="btn-show-pdf">{t('terms.content.btn.show.pdf')}</Button>
                </div>
              </Form.Item>
            </div> */}
          <div className="bottom-content">
            <div className="action-wrap">
              <strong className="title">
                {t('terms.content.studio.label')} <span>*</span>
              </strong>
              <p className="desc">{t('terms.content.studio.desc')}</p>
              <div className="input-wrap">
                <Form.Item
                  name="studio_name"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        const myRegExp = />|</g;

                        if (myRegExp.test(value)) {
                          return Promise.reject(t('terms.content.check.bracket'));
                        }

                        if (value.length < 6 || value.length > 20) {
                          return Promise.reject(t('terms.content.check.length'));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    maxLength={20}
                    autoComplete="off"
                    value={studioName}
                    onChange={(e) => {
                      setStudioName(e.currentTarget.value);
                    }}
                  />
                </Form.Item>
                <Tooltip
                  placement="top"
                  title={t('terms.content.check.length')}
                  visible={nameLength > 20 || nameLength < 6}
                  getTooltipContainer={(trigger) => trigger ?? document.body}
                  overlayStyle={{ whiteSpace: 'nowrap', maxWidth: 'none' }}
                >
                  <span className="name-length">
                    <span data-status={nameLength > 20 || nameLength < 6 ? 'warning' : ''}>
                      {nameLength}
                    </span>
                    /20
                  </span>
                </Tooltip>
              </div>
              <div className="button-wrap">
                <Button
                  htmlType="submit"
                  className="btn-complete"
                  loading={!isDoneSavedName}
                  disabled={nameLength > 20 || nameLength < 6}
                >
                  <span className="text">{t('terms.content.btn.complete')}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </ContentBlock>
  );
};

const ContentBlock = styled(Content)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1110px;
  padding: 19px 36px;
  margin-left: auto;
  margin-right: auto;

  .content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    padding: 32px 0;
    margin-bottom: 42px;
    background-color: ${({ theme }) => theme.colors.secondary.bgColor};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  }

  .top-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    max-height: 491px;
    border-bottom: 1px solid #c2cfe0;
    padding-bottom: 44px;
  }

  .bottom-content {
    flex: 0 0 auto;
    padding-top: 44px;
  }

  .title {
    font-weight: bold;
    font-size: 18px;

    span {
      color: red;
    }
  }

  .desc {
    margin-top: 17px;
    font-size: 14px;
    color: #090909;
  }

  .input-wrap {
    position: relative;
  }

  .name-length {
    position: absolute;
    right: 0;
    bottom: calc(100% + 13px);
    font-size: 14px;
    color: #c2cfe0;

    span[data-status='warning'] {
      color: red;
    }
  }

  .term-content {
    max-width: 730px;
    width: 100%;
    min-height: 200px;
    max-height: 351px;
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid #c2cfe0;
    padding: 20px;
    box-sizing: border-box;
    overflow: auto;
  }

  .checkbox-wrap {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-top: 18px;

    &[aria-label='non-checked'] {
      .ant-checkbox-inner {
        border-color: red;
      }
    }

    .btn-show-pdf {
      padding: 3px 28px;
      border: 1px solid #5100ce;
      box-sizing: border-box;
      border-radius: 3px;
      font-weight: bold;
      font-size: 14px;
      color: #5100ce;
    }
  }

  .action-wrap {
    max-width: 730px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .button-wrap {
    margin-top: 61px;
    margin-bottom: 31px;
    text-align: center;

    .btn-cancel {
      height: auto;
      margin: 0;
      padding: 12px 39px;
      border-radius: 3px;
      background-color: white;
      color: #334d6e;
    }

    .btn-complete {
      height: auto;
      margin: 0;
      margin-left: 20px;
      padding: 12px 39px;
      border-color: #5100ce;
      border-radius: 3px;
      background-color: #5100ce;
      color: white;
    }
  }
`;

export default TermsConditions;
